import React, { useState } from "react";
import { AXIS_META, RING_ORDER, cap } from "./axisMeta";
import { AxisKey, LifeGPSData } from "./types";

const VIEW = 360;
const C = VIEW / 2;
const RADII: Record<AxisKey, number> = {
  focus: 76,
  energy: 100,
  pace: 124,
  direction: 148,
};
const STROKE = 14;
const OUTER_EDGE = 148 + 14 / 2 + 2; // outer ring edge, plus a hair of air
const STROKE_ACTIVE = 18;
const HIT_STROKE = 24; // rings are 24 apart, so the hit areas tile exactly
const BASE_OPACITY = 0.9;
const DIMMED_OPACITY = 0.8;
const DURATION_S = 0.65;
const STAGGER_S = 0.08;

// Outer labels (desktop): alternate right / left, aligned to each ring's top.
const LABEL_SIDE: Record<AxisKey, "left" | "right"> = {
  direction: "right",
  pace: "left",
  energy: "right",
  focus: "left",
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type Props = {
  axes: LifeGPSData["lifeGps"]["axes"];
  coreTitle: string;
  coreSub: string;
  active: AxisKey | null; // hovered or selected ring
  coreSelected: boolean;
  updating: boolean;
  tooltipFor: (axis: AxisKey) => { title: string; body: string };
  onHover: (axis: AxisKey | null) => void;
  onSelect: (key: AxisKey | "phase") => void;
};

// Four nested signals around a centred "current phase" core. Ring length is an
// internal visualisation value, never printed as a number.
const LifeRings: React.FC<Props> = ({
  axes,
  coreTitle,
  coreSub,
  active,
  coreSelected,
  updating,
  tooltipFor,
  onHover,
  onSelect,
}) => {
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);
  // The tooltip follows the mouse only: not selection, focus or touch.
  const [mouseOver, setMouseOver] = useState<AxisKey | null>(null);
  const reduce = prefersReducedMotion();
  const tip = mouseOver && pointer ? tooltipFor(mouseOver) : null;

  const trackPointer = (e: React.PointerEvent<SVGGElement>) => {
    const rect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (rect) setPointer({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      className={`relative mx-auto aspect-square w-[280px] md:w-[320px] lg:w-[360px] ${
        updating ? "animate-pulse" : ""
      }`}
    >
      <svg
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        className="absolute inset-0 h-full w-full overflow-visible"
        role="group"
        aria-label="Life GPS rings"
      >
        {RING_ORDER.map((axis, i) => {
          const r = RADII[axis];
          const circ = 2 * Math.PI * r;
          const score = Math.max(0, Math.min(100, axes[axis].score ?? 60));
          const finalOffset = circ * (1 - score / 100);
          const isActive = active === axis;
          const meta = AXIS_META[axis];
          return (
            <g
              key={axis}
              role="button"
              tabIndex={0}
              aria-label={`${meta.label}: ${cap(axes[axis].state)}`}
              aria-pressed={isActive}
              style={{ cursor: "pointer", outline: "none" }}
              onPointerEnter={(e) => {
                if (e.pointerType === "mouse") {
                  trackPointer(e);
                  setMouseOver(axis);
                  onHover(axis);
                }
              }}
              onPointerMove={(e) => e.pointerType === "mouse" && trackPointer(e)}
              onPointerLeave={(e) => {
                if (e.pointerType === "mouse") {
                  setMouseOver(null);
                  onHover(null);
                }
              }}
              onFocus={() => onHover(axis)}
              onBlur={() => onHover(null)}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(axis);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(axis);
                }
              }}
            >
              {/* generous transparent hit area */}
              <circle
                cx={C}
                cy={C}
                r={r}
                fill="none"
                stroke="transparent"
                strokeWidth={HIT_STROKE}
                pointerEvents="stroke"
              />
              <circle
                cx={C}
                cy={C}
                r={r}
                fill="none"
                stroke="#64748b"
                strokeOpacity={0.12}
                style={{
                  strokeWidth: isActive ? STROKE_ACTIVE : STROKE,
                  transition: reduce ? undefined : "stroke-width .2s ease",
                }}
              />
              <circle
                key={`${axis}-${score}`}
                cx={C}
                cy={C}
                r={r}
                fill="none"
                stroke={meta.ring}
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={reduce ? finalOffset : circ}
                transform={`rotate(-90 ${C} ${C})`}
                style={{
                  strokeWidth: isActive ? STROKE_ACTIVE : STROKE,
                  strokeOpacity:
                    active && !isActive ? DIMMED_OPACITY : BASE_OPACITY,
                  transition: reduce
                    ? undefined
                    : "stroke-width .2s ease, stroke-opacity .2s ease",
                }}
              >
                {!reduce && (
                  <animate
                    attributeName="stroke-dashoffset"
                    from={circ}
                    to={finalOffset}
                    dur={`${DURATION_S}s`}
                    begin={`${i * STAGGER_S}s`}
                    fill="freeze"
                    calcMode="spline"
                    keyTimes="0;1"
                    keySplines="0 0 0.58 1"
                  />
                )}
              </circle>
              {/* keyboard focus indicator (not colour-only) */}
              {isActive && (
                <circle
                  cx={C}
                  cy={C}
                  r={r + STROKE_ACTIVE / 2 + 2}
                  fill="none"
                  stroke={meta.text}
                  strokeOpacity={0.5}
                  strokeWidth={1}
                  strokeDasharray="2 3"
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* centre core */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSelect("phase");
        }}
        aria-pressed={coreSelected}
        aria-label={`Current phase: ${coreTitle}, ${coreSub}`}
        className={`absolute left-1/2 top-1/2 flex aspect-square w-[90px] md:w-[100px] lg:w-[110px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border bg-[#6d4c92]/10 px-2 text-center transition ${
          coreSelected ? "border-[#6d4c92]/60" : "border-[#6d4c92]/25"
        }`}
      >
        <span className="text-[7px] lg:text-[8px] font-semibold uppercase tracking-[0.14em] text-[#6d4c92]">
          Current phase
        </span>
        <span className="mt-1 text-[11px] lg:text-[13px] font-semibold leading-tight text-slate-900">
          {coreTitle}
        </span>
        <span className="mt-0.5 text-[9px] lg:text-[10px] text-slate-500">
          {coreSub}
        </span>
      </button>

      {/* outer labels, desktop only; smaller screens use the legend below */}
      {RING_ORDER.map((axis) => {
        const side = LABEL_SIDE[axis];
        const meta = AXIS_META[axis];
        // Hug the outer ring's contour at this ring's top height so each
        // label sits beside the ring it names.
        const dy = RADII[axis];
        const edge = Math.sqrt(Math.max(0, OUTER_EDGE ** 2 - dy ** 2)) + 14;
        const offset =
          side === "right"
            ? { left: `${((C + edge) / VIEW) * 100}%` }
            : { right: `${((C + edge) / VIEW) * 100}%` };
        return (
          <div
            key={axis}
            aria-hidden="true"
            className={`pointer-events-none absolute hidden lg:flex -translate-y-1/2 items-center gap-2 whitespace-nowrap ${
              side === "left" ? "flex-row-reverse text-right" : ""
            }`}
            style={{ top: `${((C - dy) / VIEW) * 100}%`, ...offset }}
          >
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: meta.ring }}
            />
            <span className="leading-tight">
              <span
                className="block text-[11px] font-bold uppercase tracking-wider"
                style={{ color: meta.text }}
              >
                {meta.label}
              </span>
              <span className="block text-sm font-semibold text-slate-800">
                {cap(axes[axis].state)}
              </span>
            </span>
          </div>
        );
      })}

      {/* floating mini tooltip, desktop hover */}
      {tip && pointer && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-20 hidden lg:block w-56 rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
          style={{ left: pointer.x + 14, top: pointer.y + 14 }}
        >
          <p className="text-xs font-semibold text-slate-900">{tip.title}</p>
          <p className="mt-0.5 text-xs text-slate-600">{tip.body}</p>
        </div>
      )}
    </div>
  );
};

export default LifeRings;
